import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  MessageSquare,
  Send,
  RefreshCw,
  Search,
  Phone,
  Clock,
  Settings,
  User,
  ChevronLeft,
  Check,
  CheckCheck,
  AlertTriangle,
  Bell,
  UserCheck,
  Trash2
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent } from '../../components/ui/card';
import { ScrollArea } from '../../components/ui/scroll-area';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AIActorWhatsApp() {
  const { actorId } = useParams();
  const navigate = useNavigate();
  const messagesEndRef = useRef(null);
  const previousHandoffCount = useRef(0);

  const [actor, setActor] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messageInput, setMessageInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    loadActor();
    loadSessions();
  }, [actorId]);

  useEffect(() => {
    if (selectedSession) {
      loadMessages(selectedSession.session_id);
    }
  }, [selectedSession]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Auto-refresh every 10 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      if (selectedSession) {
        loadMessages(selectedSession.session_id, true);
      }
      loadSessions(true);
    }, 10000);

    return () => clearInterval(interval);
  }, [selectedSession]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadActor = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/ai/actors/${actorId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setActor(response.data);
    } catch (err) {
      console.error('Error loading actor:', err);
    }
  };

  const loadSessions = async (silent = false) => {
    try {
      if (!silent) setRefreshing(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/ai/actors/${actorId}/whatsapp/sessions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const newSessions = response.data.sessions || [];
      
      // Check for new human handoff assignments
      const currentHandoffCount = newSessions.filter(s => s.assigned_to_human).length;
      if (previousHandoffCount.current > 0 && currentHandoffCount > previousHandoffCount.current) {
        // New human handoff detected - show notification
        const newHandoffs = newSessions.filter(s => s.assigned_to_human);
        const latestHandoff = newHandoffs[0];
        toast.warning(
          `🔔 Human Agent Needed!`,
          {
            description: `Customer ${latestHandoff?.customer_phone || 'Unknown'} needs assistance`,
            duration: 10000,
            action: {
              label: 'View Chat',
              onClick: () => setSelectedSession(latestHandoff)
            }
          }
        );
      }
      previousHandoffCount.current = currentHandoffCount;
      
      setSessions(newSessions);
    } catch (err) {
      console.error('Error loading sessions:', err);
    } finally {
      if (!silent) setRefreshing(false);
    }
  };

  const loadMessages = async (sessionId, silent = false) => {
    try {
      if (!silent) setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/api/ai/actors/${actorId}/whatsapp/messages?session_id=${sessionId}&limit=100`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessages(response.data.messages.reverse() || []);
    } catch (err) {
      console.error('Error loading messages:', err);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const sendMessage = async () => {
    if (!messageInput.trim() || !selectedSession) return;

    try {
      setSending(true);
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/api/ai/actors/${actorId}/whatsapp/send`,
        {
          to_number: selectedSession.customer_phone,
          message: messageInput.trim()
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setMessageInput('');
      // Reload messages
      await loadMessages(selectedSession.session_id);
    } catch (err) {
      console.error('Error sending message:', err);
      alert('Failed to send message: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSending(false);
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString();
  };

  const formatMessageTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const deleteUserAndHistory = async (phone) => {
    if (!window.confirm(`Are you sure you want to delete this user and ALL their chat history?\n\nPhone: ${phone}\n\nThis action cannot be undone.`)) {
      return;
    }
    
    setDeleting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.delete(
        `${API}/api/ai/actors/${actorId}/whatsapp/user/${encodeURIComponent(phone)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`User deleted successfully. Removed ${response.data.deleted.sessions} sessions and ${response.data.deleted.messages} messages.`);
      
      // Clear selection and refresh sessions
      setSelectedSession(null);
      setMessages([]);
      loadSessions();
    } catch (err) {
      console.error('Error deleting user:', err);
      toast.error(err.response?.data?.detail || 'Failed to delete user');
    } finally {
      setDeleting(false);
    }
  };

  const filteredSessions = sessions.filter(session =>
    session.customer_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    session.customer_phone.includes(searchQuery)
  );

  // Count sessions needing human attention
  const humanHandoffCount = sessions.filter(s => s.assigned_to_human).length;

  return (
    <div className="h-screen flex flex-col">
      {/* Header */}
      <div className="bg-white border-b px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(`/ai/actors`)}
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-2">
                {actor?.name} - WhatsApp
                {humanHandoffCount > 0 && (
                  <span className="bg-orange-500 text-white text-sm px-2 py-0.5 rounded-full flex items-center gap-1 animate-pulse">
                    <Bell className="w-3 h-3" />
                    {humanHandoffCount} need attention
                  </span>
                )}
              </h1>
              <p className="text-sm text-muted-foreground">
                {actor?.whatsapp_phone_number || 'No WhatsApp number configured'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => loadSessions()}
              disabled={refreshing}
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(`/ai/actors/${actorId}/manage`)}
            >
              <Settings className="w-4 h-4 mr-2" />
              Settings
            </Button>
          </div>
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden">
        {/* Conversations List */}
        <div className="w-80 bg-white border-r flex flex-col">
          <div className="p-4 border-b">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search conversations..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          <ScrollArea className="flex-1">
            <div className="divide-y">
              {filteredSessions.length === 0 ? (
                <div className="p-8 text-center text-muted-foreground">
                  <MessageSquare className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                  <p className="text-sm">No conversations yet</p>
                  <p className="text-xs mt-2">Messages will appear here when customers contact you</p>
                </div>
              ) : (
                filteredSessions.map(session => (
                  <div
                    key={session.session_id}
                    onClick={() => setSelectedSession(session)}
                    className={`p-4 cursor-pointer hover:bg-slate-50 transition-colors relative ${
                      selectedSession?.session_id === session.session_id ? 'bg-slate-100' : ''
                    } ${
                      session.assigned_to_human 
                        ? 'border-l-4 border-l-orange-500 bg-orange-50 hover:bg-orange-100' 
                        : ''
                    }`}
                  >
                    {/* Pulsing indicator for human-assigned chats */}
                    {session.assigned_to_human && (
                      <span className="absolute top-2 right-2 flex h-3 w-3">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-3 w-3 bg-orange-500"></span>
                      </span>
                    )}
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-semibold text-sm truncate">
                            {session.customer_name || session.customer_phone}
                          </p>
                          {session.unread_count > 0 && (
                            <span className="bg-green-500 text-white text-xs px-2 py-0.5 rounded-full">
                              {session.unread_count}
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground truncate">
                          {session.customer_phone}
                        </p>
                      </div>
                      <span className="text-xs text-muted-foreground whitespace-nowrap ml-2">
                        {formatTime(session.last_message_at)}
                      </span>
                    </div>
                    <div className="mt-1 flex items-center gap-2 text-xs">
                      <span className="text-muted-foreground">
                        {session.message_count} messages
                      </span>
                      {session.assigned_to_human && (
                        <span className="bg-orange-500 text-white px-2 py-0.5 rounded flex items-center gap-1 font-medium animate-pulse">
                          <AlertTriangle className="w-3 h-3" />
                          Needs Human
                        </span>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </ScrollArea>
        </div>

        {/* Messages View */}
        <div className="flex-1 flex flex-col bg-slate-50">
          {selectedSession ? (
            <>
              {/* Chat Header */}
              <div className="bg-white border-b px-6 py-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="font-semibold">
                      {selectedSession.customer_name || selectedSession.customer_phone}
                    </h2>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Phone className="w-3 h-3" />
                      {selectedSession.customer_phone}
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right text-sm">
                      <p className="text-muted-foreground">
                        {selectedSession.message_count} messages
                      </p>
                      {selectedSession.assigned_to_human && (
                        <span className="inline-flex items-center gap-1 text-orange-700 bg-orange-100 px-2 py-1 rounded text-xs mt-1 font-medium">
                          <UserCheck className="w-3 h-3" />
                          Assigned to You
                        </span>
                      )}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                      onClick={() => deleteUserAndHistory(selectedSession.customer_phone)}
                      disabled={deleting}
                    >
                      <Trash2 className={`w-4 h-4 mr-1 ${deleting ? 'animate-spin' : ''}`} />
                      {deleting ? 'Deleting...' : 'Delete User'}
                    </Button>
                  </div>
                </div>
              </div>
              
              {/* Human Handoff Banner */}
              {selectedSession.assigned_to_human && (
                <div className="bg-orange-500 text-white px-6 py-3 flex items-center gap-3">
                  <AlertTriangle className="w-5 h-5 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="font-semibold text-sm">Human Agent Required</p>
                    <p className="text-xs opacity-90">This customer has requested to speak with a human or AI could not assist. Please respond.</p>
                  </div>
                  <Button 
                    variant="secondary"
                    size="sm"
                    className="bg-white text-orange-600 hover:bg-orange-50"
                    onClick={async () => {
                      try {
                        const token = localStorage.getItem('token');
                        await axios.put(
                          `${API}/api/ai/actors/${actorId}/whatsapp/sessions/${selectedSession.session_id}`,
                          { assigned_to_human: false },
                          { headers: { Authorization: `Bearer ${token}` } }
                        );
                        setSelectedSession({ ...selectedSession, assigned_to_human: false });
                        loadSessions(true);
                        toast.success('Chat marked as resolved');
                      } catch (err) {
                        console.error('Error resolving chat:', err);
                        toast.error('Failed to resolve chat');
                      }
                    }}
                  >
                    <Check className="w-4 h-4 mr-1" />
                    Mark Resolved
                  </Button>
                </div>
              )}

              {/* Messages */}
              <ScrollArea className="flex-1 p-6">
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {messages.map((msg, index) => (
                      <div
                        key={msg.id}
                        className={`flex ${msg.direction === 'outbound' ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={`max-w-md rounded-lg px-4 py-2 ${
                            msg.direction === 'outbound'
                              ? 'bg-green-500 text-white'
                              : 'bg-white text-slate-900 border'
                          }`}
                        >
                          <p className="text-sm whitespace-pre-wrap break-words">{msg.message_body}</p>
                          <div className={`flex items-center justify-end gap-1 mt-1 text-xs ${
                            msg.direction === 'outbound' ? 'text-green-100' : 'text-muted-foreground'
                          }`}>
                            <span>{formatMessageTime(msg.timestamp)}</span>
                            {msg.direction === 'outbound' && (
                              msg.status === 'delivered' || msg.status === 'read' ? (
                                <CheckCheck className="w-3 h-3" />
                              ) : (
                                <Check className="w-3 h-3" />
                              )
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                    <div ref={messagesEndRef} />
                  </div>
                )}
              </ScrollArea>

              {/* Message Input */}
              <div className="bg-white border-t p-4">
                <div className="flex gap-2">
                  <Textarea
                    placeholder="Type a message..."
                    value={messageInput}
                    onChange={(e) => setMessageInput(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        sendMessage();
                      }
                    }}
                    rows={2}
                    className="resize-none"
                  />
                  <Button
                    onClick={sendMessage}
                    disabled={!messageInput.trim() || sending}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Press Enter to send, Shift+Enter for new line
                </p>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-muted-foreground">
              <div className="text-center">
                <MessageSquare className="w-16 h-16 mx-auto mb-4 text-slate-300" />
                <p className="text-lg font-medium">Select a conversation</p>
                <p className="text-sm mt-2">Choose a conversation from the list to view messages</p>
              </div>
            </div>
          )}
        </div>

        {/* Customer Info Sidebar */}
        {selectedSession && (
          <div className="w-72 bg-white border-l p-6">
            <h3 className="font-semibold mb-4">Customer Info</h3>
            <div className="space-y-4">
              <div>
                <p className="text-sm text-muted-foreground">Phone Number</p>
                <p className="font-medium">{selectedSession.customer_phone}</p>
              </div>
              {selectedSession.customer_name && (
                <div>
                  <p className="text-sm text-muted-foreground">Name</p>
                  <p className="font-medium">{selectedSession.customer_name}</p>
                </div>
              )}
              <div>
                <p className="text-sm text-muted-foreground">Messages</p>
                <p className="font-medium">{selectedSession.message_count}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Status</p>
                <p className="font-medium capitalize">{selectedSession.status}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Started</p>
                <p className="font-medium">{new Date(selectedSession.created_at).toLocaleString()}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Last Message</p>
                <p className="font-medium">{new Date(selectedSession.last_message_at).toLocaleString()}</p>
              </div>
              
              {/* Delete User Button */}
              <div className="pt-4 border-t mt-4">
                <Button
                  variant="outline"
                  className="w-full text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                  onClick={() => deleteUserAndHistory(selectedSession.customer_phone)}
                  disabled={deleting}
                >
                  <Trash2 className={`w-4 h-4 mr-2 ${deleting ? 'animate-spin' : ''}`} />
                  {deleting ? 'Deleting...' : 'Delete User & History'}
                </Button>
                <p className="text-xs text-muted-foreground mt-2 text-center">
                  This will permanently delete all messages and sessions for this user.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
